import type { ResourceVideoSource } from "@/features/resource-editor/resourceVideoSource";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bookmark, Film, GripVertical, Scissors } from "lucide-react";
import type { CoachingClip, CoachingNote } from "@/lib/types";
import {
  clamp,
  clipSeverity,
  formatClock,
  formatDuration,
  FRAME,
  snapTime,
  type ClipWindow,
} from "../clipTrack";
import { useTimelineFilmstrip } from "../hooks/useTimelineFilmstrip";

export type ClipTrackProps = {
  resourceSource?:ResourceVideoSource|null;
  canTrim?:boolean;
  /** The Resource editor's dock has no thumbnail strip (plan decision 9). */
  filmstrip?:boolean;
  resourceMoment?:{resourceId:string;revisionId:string;recordingId:string;recordingGeneration:string;seconds:number;label:string};
  onResourceUnavailable?:()=>void;
  timelineView?:TimelineView;
  onTimelineViewChange?:(view:TimelineView)=>void;
  duration: number;
  playhead: number;
  onSeek: (seconds: number) => void;
  clipWindow: ClipWindow | null;
  onClipWindowChange: (window: ClipWindow) => void;
  inClipMode: boolean;
  notes: CoachingNote[];
  clips: CoachingClip[];
  activeNoteId: number | null;
  activeClipId: number | null;
  onJumpToNote: (note: CoachingNote) => void;
  onOpenClip: (clip: CoachingClip) => void;
  matchName: string;
  slotName: string;
  matchId?: string | null;
  slot?: string | null;
};

type DragKind = "playhead" | "in" | "out";
export type TimelineView = "full" | "detail";
const TICK_COUNT = 7;
const FILMSTRIP_FRAMES = 12;
const MOBILE_FILMSTRIP_FRAMES = new Set([0, 2, 4, 7, 9, 11]);

/** Real-media implementation of the approved compact Review timeline. */
export function ClipTrack({
  duration,
  playhead,
  onSeek,
  clipWindow,
  onClipWindowChange,
  inClipMode,
  notes,
  clips,
  activeNoteId,
  activeClipId,
  onJumpToNote,
  onOpenClip,
  matchName,
  slotName,
  matchId,
  slot, resourceSource, onResourceUnavailable, canTrim=true, filmstrip:showFilmstrip=true, resourceMoment, timelineView:controlledView, onTimelineViewChange,
}: ClipTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<DragKind | null>(null);
  const [localView, setLocalView] = useState<TimelineView>(
    inClipMode ? "detail" : "full",
  );
  const timelineView=controlledView??localView;
  const setTimelineView=(view:TimelineView)=>{if(controlledView===undefined)setLocalView(view);onTimelineViewChange?.(view);};
  // Keep the detail viewport fixed while a trim handle is moving. Without
  // this snapshot, changing either boundary also re-centres the 60-second
  // detail window on every pointer event. The opposite boundary keeps the
  // same timestamp but slides across the screen, which makes the whole clip
  // selection look as though it is growing or shrinking from both ends.
  const [trimViewport, setTrimViewport] = useState<ClipWindow | null>(null);

  useEffect(() => { if(controlledView===undefined)setLocalView(inClipMode ? "detail" : "full"); }, [inClipMode,controlledView]);

  const hasTimeline = duration > 0;
  const safeDuration = hasTimeline ? duration : 1;
  const clipDuration = clipWindow ? clipWindow.end - clipWindow.start : 0;
  const severity = clipSeverity(clipWindow);
  // A match can own separate recordings for each half. Only plot saved
  // feedback anchored to the recording currently on screen; otherwise a
  // first-half timestamp can masquerade as a marker on the second half.
  const timelineNotes = useMemo(
    () => notes.filter((note) => !slot || !note.slot || note.slot === slot),
    [notes, slot],
  );
  const timelineClips = useMemo(
    () => clips.filter((clip) => !slot || clip.slot === slot),
    [clips, slot],
  );

  const calculatedVisibleWindow = useMemo(() => {
    if (!hasTimeline || timelineView === "full") return { start: 0, end: safeDuration };
    const requestedSpan = clipWindow
      ? Math.max(60, Math.min(140, clipDuration + 20))
      : 60;
    const span = Math.min(safeDuration, requestedSpan);
    const center = clipWindow ? (clipWindow.start + clipWindow.end) / 2 : playhead;
    const rawStart = clamp(center - span / 2, 0, Math.max(0, safeDuration - span));
    const pageSize = span / 2;
    const start = clipWindow
      ? rawStart
      : clamp(Math.floor(rawStart / pageSize) * pageSize, 0, Math.max(0, safeDuration - span));
    return { start, end: start + span };
  }, [clipDuration, clipWindow, hasTimeline, playhead, safeDuration, timelineView]);
  const visibleWindow = trimViewport ?? calculatedVisibleWindow;
  const filmstrip = useTimelineFilmstrip({
    resourceSource,onResourceUnavailable,
    matchId,
    slot,
    start: visibleWindow.start,
    end: visibleWindow.end,
    frames: FILMSTRIP_FRAMES,
    enabled: hasTimeline && showFilmstrip,
  });

  const visibleSpan = Math.max(FRAME, visibleWindow.end - visibleWindow.start);
  const pctNumber = useCallback(
    (seconds: number) => clamp((seconds - visibleWindow.start) / visibleSpan, 0, 1) * 100,
    [visibleSpan, visibleWindow.start],
  );
  const pct = useCallback((seconds: number) => `${pctNumber(seconds)}%`, [pctNumber]);
  const noteSeconds = useMemo(
    () => timelineNotes.map((note) => Number(note.timestamp_seconds ?? -1)).filter((time) => time >= 0),
    [timelineNotes],
  );

  const posToTime = useCallback(
    (clientX: number): number => {
      const element = trackRef.current;
      if (!element) return visibleWindow.start;
      const rect = element.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return visibleWindow.start + ratio * visibleSpan;
    },
    [visibleSpan, visibleWindow.start],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (event: PointerEvent) => {
      const time = posToTime(event.clientX);
      if (dragging === "playhead") {
        onSeek(time);
        return;
      }
      if (!canTrim||!clipWindow) return;
      const snapped = snapTime(time, noteSeconds);
      if (dragging === "in") {
        onClipWindowChange({
          start: Math.min(snapped, clipWindow.end - FRAME * 5),
          end: clipWindow.end,
        });
      } else {
        onClipWindowChange({
          start: clipWindow.start,
          end: Math.max(snapped, clipWindow.start + FRAME * 5),
        });
      }
    };
    const finishDrag = () => {
      setDragging(null);
      setTrimViewport(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", finishDrag);
    window.addEventListener("pointercancel", finishDrag);
    window.addEventListener("lostpointercapture", finishDrag);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", finishDrag);
      window.removeEventListener("pointercancel", finishDrag);
      window.removeEventListener("lostpointercapture", finishDrag);
    };
  }, [clipWindow, dragging, noteSeconds, onClipWindowChange, onSeek, posToTime,canTrim]);

  const beginTrim = useCallback(
    (kind: "in" | "out") => {
      if(!canTrim)return;
      setTrimViewport(calculatedVisibleWindow);
      setDragging(kind);
    },
    [calculatedVisibleWindow,canTrim],
  );

  const ticks = Array.from({ length: TICK_COUNT }, (_, index) => {
    const ratio = index / (TICK_COUNT - 1);
    return { ratio, time: visibleWindow.start + visibleSpan * ratio };
  });
  const visibleNotes = timelineNotes.filter((note) => {
    const time = Number(note.timestamp_seconds ?? -1);
    return time >= visibleWindow.start && time <= visibleWindow.end;
  });
  const visibleClips = timelineClips.filter(
    (clip) => clip.end_seconds >= visibleWindow.start && clip.start_seconds <= visibleWindow.end,
  );

  const setHandleFromKeyboard = (kind: "in" | "out", delta: number) => {
    if (!canTrim||!clipWindow) return;
    if (kind === "in") {
      onClipWindowChange({
        start: clamp(clipWindow.start + delta, 0, Math.max(0, clipWindow.end - FRAME * 5)),
        end: clipWindow.end,
      });
    } else {
      onClipWindowChange({
        start: clipWindow.start,
        end: clamp(clipWindow.end + delta, clipWindow.start + FRAME * 5, duration),
      });
    }
  };

  const selectionStart = clipWindow ? Math.max(clipWindow.start, visibleWindow.start) : 0;
  const selectionEnd = clipWindow ? Math.min(clipWindow.end, visibleWindow.end) : 0;

  return (
    <div className="review-editor-track" data-testid="coach-review-clip-track">
      <div className="editor-timeline-heading">
        <span className="editor-lane-name">
          <Film size={13} aria-hidden />
          <span>{matchName || "Match recording"}</span>
          <span className="editor-saved-count">
            {resourceSource!==undefined?(inClipMode?'Clip Resource':resourceMoment?'Saved moment Resource':'Moment Resource'):<>{timelineNotes.length} {timelineNotes.length === 1 ? "moment" : "moments"}
            <span aria-hidden="true"> · </span>
            {timelineClips.length} {timelineClips.length === 1 ? "clip" : "clips"}</>}
          </span>
        </span>
        <div className="editor-zoom" role="group" aria-label="Timeline view">
          <button type="button" className="editor-button editor-zoom-button" aria-pressed={timelineView === "full"} onClick={() => setTimelineView("full")}>Full recording</button>
          <button type="button" className="editor-button editor-zoom-button" aria-pressed={timelineView === "detail"} onClick={() => setTimelineView("detail")}>Detail</button>
        </div>
      </div>

      <div className="editor-axis-wrap">
        <div className="editor-axis">
          <div className="editor-ruler" aria-hidden="true">
            {ticks.map(({ ratio, time }, index) => (
              <span
                key={`${ratio}-${time}`}
                className={["editor-tick", index === 0 ? "is-first" : "", index === TICK_COUNT - 1 ? "is-last" : ""].filter(Boolean).join(" ")}
                style={{ left: `${ratio * 100}%` }}
              >
                <span>{formatClock(time)}</span>
              </span>
            ))}
          </div>

          <div
            ref={trackRef}
            className="editor-footage"
            data-testid="clip-track-scrub"
            onPointerDown={(event) => {
              if (!hasTimeline) return;
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              onSeek(posToTime(event.clientX));
              setDragging("playhead");
            }}
          >
            {showFilmstrip ? <div className="editor-footage-media" data-testid="coach-review-thumbnail-strip" data-ready={filmstrip.status === "ready"} aria-hidden="true">
              {filmstrip.url
                ? Array.from({ length: FILMSTRIP_FRAMES }, (_, index) => (
                    <span
                      key={index}
                      className="editor-filmstrip-frame"
                      data-mobile-visible={MOBILE_FILMSTRIP_FRAMES.has(index)}
                      style={{
                        backgroundImage: `url(${filmstrip.url})`,
                        backgroundPosition: `${(index / (FILMSTRIP_FRAMES - 1)) * 100}% 50%`,
                        backgroundSize: `${FILMSTRIP_FRAMES * 100}% 100%`,
                      }}
                    />
                  ))
                : Array.from({ length: FILMSTRIP_FRAMES }, (_, index) => (
                    <span
                      key={index}
                      className="editor-filmstrip-frame editor-footage-fallback"
                      data-mobile-visible={MOBILE_FILMSTRIP_FRAMES.has(index)}
                    />
                  ))}
            </div> : null}
            <span className="editor-footage-label"><Film size={11} aria-hidden />{slotName}</span>
            {!hasTimeline ? <span className="editor-no-video" data-testid="clip-track-no-video">Select a match to load the video</span> : null}

            {hasTimeline && inClipMode && clipWindow && selectionEnd > selectionStart ? (
              <div
                className={["editor-selection", severity === "over" ? "is-over" : ""].filter(Boolean).join(" ")}
                data-testid="clip-track-window"
                style={{ left: pct(selectionStart), width: `${pctNumber(selectionEnd) - pctNumber(selectionStart)}%` }}
              ><span>{formatDuration(clipDuration)}</span></div>
            ) : null}

            {hasTimeline && inClipMode && clipWindow ? (
              <>
                <ClipHandle disabled={!canTrim} kind="in" position={pct(clipWindow.start)} value={clipWindow.start} min={0} max={Math.max(0, clipWindow.end - FRAME * 5)} onPointerDown={(event) => { event.stopPropagation(); beginTrim("in"); }} onKeyboardChange={(delta) => setHandleFromKeyboard("in", delta)} />
                <ClipHandle disabled={!canTrim} kind="out" position={pct(clipWindow.end)} value={clipWindow.end} min={clipWindow.start + FRAME * 5} max={duration} onPointerDown={(event) => { event.stopPropagation(); beginTrim("out"); }} onKeyboardChange={(delta) => setHandleFromKeyboard("out", delta)} />
              </>
            ) : null}
          </div>

          {hasTimeline ? <div className="editor-cursor" style={{ left: pct(playhead) }} data-testid="clip-track-playhead" hidden={playhead < visibleWindow.start || playhead > visibleWindow.end} /> : null}

          {hasTimeline&&resourceMoment&&resourceSource
            &&resourceMoment.recordingId===resourceSource.recording.recording_id
            &&resourceMoment.recordingGeneration===resourceSource.recording.recording_generation
            &&resourceMoment.seconds>=visibleWindow.start&&resourceMoment.seconds<=visibleWindow.end&&<button
              type="button" className="editor-moment is-current" style={{left:pct(resourceMoment.seconds)}}
              data-resource-id={resourceMoment.resourceId} data-resource-revision={resourceMoment.revisionId}
              aria-label={`Seek to saved moment ${resourceMoment.label}`} aria-pressed="true"
              onPointerDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();onSeek(resourceMoment.seconds);}}>
              <Bookmark size={12} aria-hidden/>
            </button>}
          {hasTimeline ? visibleNotes.map((note) => {
            const time = Number(note.timestamp_seconds ?? 0);
            return (
              <button
                key={note.id}
                type="button"
                className={["editor-moment", activeNoteId === note.id ? "is-current" : ""].filter(Boolean).join(" ")}
                style={{ left: pct(time) }}
                title={`${formatClock(time)} · ${note.title}`}
                aria-pressed={activeNoteId === note.id}
                data-testid="clip-track-note-mark"
                data-marker-kind="moment"
                data-coach-note-id={note.id}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => { event.stopPropagation(); onJumpToNote(note); }}
              ><Bookmark size={12} aria-hidden /><span className="sr-only">Seek to {note.title}</span></button>
            );
          }) : null}

          {hasTimeline ? visibleClips.map((clip) => {
            const visibleStart = Math.max(clip.start_seconds, visibleWindow.start);
            const visibleEnd = Math.min(clip.end_seconds, visibleWindow.end);
            return (
              <button
                key={clip.id}
                type="button"
                className={["editor-saved-clip", activeClipId === clip.id ? "is-current" : ""].filter(Boolean).join(" ")}
                style={{
                  left: pct(visibleStart),
                  width: `${Math.max(0, pctNumber(visibleEnd) - pctNumber(visibleStart))}%`,
                }}
                title={`${clip.title} · ${formatDuration(clip.duration_seconds)}`}
                aria-label={`Select clip ${clip.title} at ${formatClock(clip.start_seconds)}`}
                aria-pressed={activeClipId === clip.id}
                data-testid="clip-track-existing-clip"
                data-marker-kind="clip"
                data-coach-clip-id={clip.id}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenClip(clip);
                }}
              >
                <Scissors size={11} aria-hidden />
                <span className="sr-only">{clip.title}</span>
              </button>
            );
          }) : null}
        </div>
      </div>
      {showFilmstrip && (filmstrip.status === "loading" || filmstrip.status === "error") ? (
        <div className="editor-preview-status" role="status" aria-live="polite">
          <span>{filmstrip.status === "loading" ? "Loading previews" : "Previews unavailable"}</span>
          {filmstrip.status === "error" ? (
            <button
              type="button"
              className="editor-button editor-preview-retry"
              aria-label="Retry timeline previews"
              onKeyDown={(event) => {
                // Keep native Space activation out of the global playback shortcut.
                if (event.key === " ") event.stopPropagation();
              }}
              onClick={filmstrip.retry}
            >Retry</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ClipHandle({ kind, position, value, min, max, onPointerDown, onKeyboardChange,disabled=false }: {
  disabled?:boolean;
  kind: "in" | "out";
  position: string;
  value: number;
  min: number;
  max: number;
  onPointerDown: (event: React.PointerEvent) => void;
  onKeyboardChange: (delta: number) => void;
}) {
  return (
    <div
      role="slider"
      tabIndex={disabled?-1:0}
      aria-disabled={disabled}
      aria-label={`Clip ${kind.toUpperCase()} point`}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={formatClock(value)}
      onPointerDown={(event) => { if(disabled)return; event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); onPointerDown(event); }}
      onKeyDown={(event) => {
        if (disabled||event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        onKeyboardChange(direction * (event.shiftKey ? 1 : FRAME));
      }}
      className={`editor-trim editor-trim-${kind}`}
      style={{ left: position }}
      data-testid={`clip-track-handle-${kind}`}
    >
      <span className="editor-trim-label">{kind}</span>
      <span className="editor-trim-grip" aria-hidden="true"><GripVertical size={12} /></span>
    </div>
  );
}
